/**
 * 從 localStorage 讀使用者的投資組合，並支援 cross-tab + same-tab 即時更新。
 *
 * PortfolioPanel 編輯後會 dispatch 'invest-copilot:portfolio-changed' event，
 * 任何呼叫 useMyPosition() 的元件都會自動 re-render。
 */

import { useEffect, useMemo, useState } from 'react';
import type { Market, Position } from '@invest-copilot/shared';

export const PORTFOLIO_KEY = 'invest-copilot.portfolio.v1';
export const PORTFOLIO_CHANGE_EVENT = 'invest-copilot:portfolio-changed';

function loadPortfolio(): Position[] {
  try {
    const raw = localStorage.getItem(PORTFOLIO_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Position[]) : [];
  } catch {
    return [];
  }
}

function usePortfolioPositions(): Position[] {
  const [all, setAll] = useState<Position[]>(() => loadPortfolio());
  useEffect(() => {
    const refresh = () => setAll(loadPortfolio());
    window.addEventListener(PORTFOLIO_CHANGE_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(PORTFOLIO_CHANGE_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);
  return all;
}

/** PortfolioPanel 改完 portfolio 後呼叫，讓全頁 hook 同步更新 */
export function emitPortfolioChange(): void {
  try {
    window.dispatchEvent(new Event(PORTFOLIO_CHANGE_EVENT));
  } catch {/**/}
}

export interface MyPositionSummary {
  positions: Position[];
  totalQty: number;
  /** 加權平均成本 = sum(qty × costBasis) / sum(qty) */
  avgCost: number;
  totalCost: number;
  /** 最早一筆的 buyDate（適合當「我何時進場」基準） */
  firstBuyDate: string;
}

/**
 * 取得指定 symbol 的使用者持倉摘要；無持倉 → null
 */
export function useMyPosition(symbol: string): MyPositionSummary | null {
  const all = usePortfolioPositions();

  const target = symbol.toUpperCase();
  const positions = all.filter((p) => p.symbol.toUpperCase() === target);
  if (positions.length === 0) return null;

  const totalQty = positions.reduce((s, p) => s + p.qty, 0);
  const totalCost = positions.reduce((s, p) => s + p.qty * p.costBasis, 0);
  const avgCost = totalQty > 0 ? totalCost / totalQty : 0;
  // 取最早的 buyDate
  const firstBuyDate = positions
    .map((p) => p.buyDate)
    .filter((d) => typeof d === 'string' && d.length > 0)
    .sort()[0] ?? '';

  return { positions, totalQty, avgCost, totalCost, firstBuyDate };
}

export function usePortfolioSymbols(market?: Market): string[] {
  const all = usePortfolioPositions();
  return useMemo(() => {
    const seen = new Set<string>();
    const symbols: string[] = [];
    for (const position of all) {
      if (market && position.market !== market) continue;
      const symbol = position.symbol.toUpperCase();
      if (!symbol || seen.has(symbol)) continue;
      seen.add(symbol);
      symbols.push(symbol);
    }
    return symbols;
  }, [all, market]);
}
